import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// `<Chat>` — the Claude-like END-USER conversation view (fn-48.3,
// R4/R5/R10/R12). Same adapter seam + stream reducer as the operator
// `<SessionView>`, different persona: grouped bubbles with sparse
// timestamps, friendly activity lines with an expandable technical
// disclosure, a working indicator, quiet reconnect handling, terminal
// end-copy replacing the composer — and NO raw ids, status enums,
// tool names, or transport errors anywhere in the rendered output.
//
// Composition (lifted from SessionView, presentation swapped):
//   `getSession` (header: agent name via `listAgents`, subject via
//   `deriveSubject` — never an id) + `getSessionEvents` seed +
//   `useSessionStream` live frames + per-session state resets.
//
// **Effective status**: stream `RuntimeStatus` once live, seeded CP
// row status before — both through `isTerminalStatus()`.
//
// **Pending gate** (fn-48.4): when a HITL gate is open, the
// `.sbui-chat-gate` region renders the in-chat action cards — the
// plain-language approval card, or the interactive `ask_user_question`
// answer form for elicitation rows (`ack_kind ===
// 'custom_tool_result_immediate'`). The actionable approval row is
// resolved by polling `listApprovals` while the gate is open
// (`usePendingApprovalRow`, ported from SessionView); until a row
// exists (webhook lag) the awaiting note renders — never an
// actionable skeleton. Under `readOnly` the polling is skipped and
// the cards render informational-only from the reducer's
// `pendingApproval`. The composer is disabled-with-reason while the
// gate is open.
//
// **Per-session resets** (.flow memory: per-session-ui-state, 3
// rounds): composer-scoped state resets via render-time ref compare +
// epoch guard; the log (disclosure state + stick-to-bottom refs) and
// the composer (uncontrolled draft) are keyed by sessionId with
// DISTINCT key prefixes (duplicate sibling keys corrupt
// reconciliation — fn-47).
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ActivityBlock } from './internal/chat/activity.js';
import { ApprovalActionCard } from './internal/chat/approval-action-card.js';
import { ASK_USER_TOOL_NAME, AskUserFallbackCard, AskUserForm, } from './internal/chat/ask-user-form.js';
import { BubbleGroup, PENDING_BUBBLE_ID, buildChatItems, } from './internal/chat/bubbles.js';
import { ChatComposer } from './internal/chat/chat-composer.js';
import { formatDurationMs } from './internal/chat/duration.js';
import { MarkdownContent } from './internal/chat/markdown-content.js';
import { ReviewMatterCard, isReviewMatterTool } from './internal/chat/review-matter-card.js';
import { usePrefersReducedMotion, useStickToBottom } from './internal/chat/stick-to-bottom.js';
import { computeTurnTimings } from './internal/chat/turn-timings.js';
import { usePendingApprovalRow } from './internal/chat/use-pending-approval-row.js';
import { usePlatformAdapter } from './internal/context.js';
import { deriveSubject } from './internal/conversations/subject.js';
import { useAgentNames } from './internal/conversations/use-agent-names.js';
import { AGENT_RESPONDING, BACK_LABEL, CHAT_VIEW_ONLY, CONVERSATION_ENDED, ERROR_COPY, FALLBACK_AGENT_NAME, RECONNECTING_NOTE, RESPOND_TO_CONTINUE, STATUS_LINES, conversationWith, } from './internal/copy/copy.js';
import { selectActivePreview } from './internal/stream/reduce-stream.js';
import { useSessionStream } from './internal/stream/use-session-stream.js';
import { isTerminalStatus } from './status.js';
/** Default cadence for the pending-approval row lookup (console parity: 3s). */
const DEFAULT_APPROVAL_POLL_MS = 3_000;
/** Count wire user messages whose trimmed content matches. */
function countEchoMatches(messages, content) {
    const needle = content.trim();
    let count = 0;
    for (const m of messages) {
        if (m.kind === 'user' && m.isBootstrap !== true && m.content.trim() === needle)
            count += 1;
    }
    return count;
}
/**
 * Classify a `sendMessage` rejection into a friendly send-error bucket. A CP
 * 409 `session_busy` (the agent is still responding to a previous turn) surfaces
 * as `'busy'` — not a transport failure — so the composer can say "wasn't sent,
 * try again in a moment" instead of the generic "couldn't send".
 *
 * Shape-based + adapter-agnostic (the `sendMessage` seam has no typed error):
 * detects a tRPC `CONFLICT` code OR a `session_busy` token in the message. Any
 * adapter whose 409 carries neither falls through to `'network'` — fully
 * backward-compatible for existing consumers (`/preview`, the embeddable web
 * app). Pure + exported for unit tests.
 */
export function classifySendError(err) {
    if (typeof err === 'object' && err !== null) {
        const data = err.data;
        if (typeof data === 'object' && data !== null) {
            const code = data.code;
            if (code === 'CONFLICT')
                return 'busy';
        }
    }
    const message = err instanceof Error ? err.message : typeof err === 'string' ? err : '';
    if (message.includes('session_busy'))
        return 'busy';
    return 'network';
}
export function Chat(props) {
    const adapter = usePlatformAdapter();
    const sessionId = props.sessionId;
    const readOnly = props.readOnly === true;
    const agentNames = useAgentNames();
    // ── Session row (header metadata + pre-stream status seed) ──────
    const [row, setRow] = useState({ session: null, failed: false });
    useEffect(() => {
        let cancelled = false;
        setRow({ session: null, failed: false });
        adapter
            .getSession(sessionId)
            .then((session) => {
            if (!cancelled)
                setRow({ session, failed: false });
        })
            .catch(() => {
            if (!cancelled)
                setRow({ session: null, failed: true });
        });
        return () => {
            cancelled = true;
        };
    }, [adapter, sessionId]);
    // ── Transcript seed (one-shot history fetch) ────────────────────
    const [seed, setSeed] = useState({ events: undefined, failed: false });
    useEffect(() => {
        let cancelled = false;
        setSeed({ events: undefined, failed: false });
        adapter
            .getSessionEvents({ sessionId })
            .then((page) => {
            if (!cancelled)
                setSeed({ events: page.data, failed: false });
        })
            .catch(() => {
            // The live stream still paints the tail; note the gap quietly.
            if (!cancelled)
                setSeed({ events: undefined, failed: true });
        });
        return () => {
            cancelled = true;
        };
    }, [adapter, sessionId]);
    const stream = useSessionStream({
        sessionId,
        ...(seed.events !== undefined ? { seedEvents: seed.events } : {}),
    });
    // ── Effective status: live RuntimeStatus once streaming, seeded CP
    // row status before ('unknown' = no status frame yet).
    const effectiveStatus = stream.status !== 'unknown' ? stream.status : (row.session?.status ?? 'unknown');
    const terminal = isTerminalStatus(effectiveStatus);
    const working = effectiveStatus === 'running';
    // ── Reconnect note: only after the stream WAS open for THIS session
    // (the initial optimistic 'connecting' is not a reconnect).
    const wasOpenRef = useRef(false);
    if (stream.connection === 'open')
        wasOpenRef.current = true;
    const reconnecting = stream.connection === 'connecting' && wasOpenRef.current;
    // ── Optimistic just-sent bubbles + composer-scoped state ─────────
    //
    // Each in-flight send is tracked as an entry with its own id and an
    // echo BASELINE: the number of matching user messages already on the
    // wire (plus earlier same-content pending entries) at send time. An
    // entry clears only when the match count EXCEEDS its baseline — so a
    // repeated message ("yes") is never cleared by an OLDER identical
    // message, and two un-echoed sends each keep their own bubble.
    const [pendingSends, setPendingSends] = useState([]);
    const [sending, setSending] = useState(false);
    // The friendly send-failure bucket (null = no error). `'busy'` is the CP 409
    // `session_busy` (agent still responding); `'network'` is a transport failure.
    const [sendError, setSendError] = useState(null);
    // Epoch guard: a send in flight for the PREVIOUS session must not
    // mutate composer state after a sessionId switch.
    const sendEpochRef = useRef(0);
    const pendingIdRef = useRef(0);
    // Latest wire messages for baseline computation inside the send
    // callback (which must not re-bind per stream frame).
    const messagesRef = useRef(stream.messages);
    messagesRef.current = stream.messages;
    // Session-id transition reset (render-time setState gated on a value
    // change — the documented adjust-on-prop-change pattern).
    const chatSessionRef = useRef(sessionId);
    if (chatSessionRef.current !== sessionId) {
        chatSessionRef.current = sessionId;
        sendEpochRef.current += 1;
        wasOpenRef.current = false;
        setPendingSends([]);
        setSendError(null);
        setSending(false);
    }
    // Clear each optimistic bubble once the wire echoes ITS message back
    // (match count rises above the entry's baseline).
    useEffect(() => {
        setPendingSends((prev) => {
            if (prev.length === 0)
                return prev;
            const next = prev.filter((p) => countEchoMatches(stream.messages, p.content) <= p.baseline);
            return next.length === prev.length ? prev : next;
        });
    }, [stream.messages]);
    const visibleMessages = useMemo(() => {
        // Bootstrap rows (the CP-injected <system-bootstrap> exchange) must
        // never render to a client. `buildChatItems` filters defensively
        // too — this keeps the subject derivation below bootstrap-free.
        const base = stream.messages.filter((m) => !(m.kind === 'user' && m.isBootstrap === true) &&
            !(m.kind === 'assistant' && m.isBootstrapAck === true));
        if (pendingSends.length === 0)
            return base;
        return [
            ...base,
            ...pendingSends.map((p) => ({ kind: 'user', id: p.id, content: p.content })),
        ];
    }, [stream.messages, pendingSends]);
    // ── Header: agent name (never an id) + subject ────────────────────
    const resolvedAgentName = row.session !== null ? (agentNames.get(row.session.agent_id) ?? null) : null;
    const agentName = resolvedAgentName ?? FALLBACK_AGENT_NAME;
    const firstUserMessage = visibleMessages.find((m) => m.kind === 'user' && !m.id.startsWith(PENDING_BUBBLE_ID))?.content ?? null;
    const subject = row.session !== null
        ? deriveSubject({ session: row.session, firstUserMessage, agentName: resolvedAgentName })
        : conversationWith(agentName);
    // ── Pending HITL gate: in-chat action cards (fn-48.4) ────────────
    const preStreamGateFallback = stream.status === 'unknown' && row.session?.status === 'awaiting_approval';
    const gateOpen = stream.pendingApproval !== null || preStreamGateFallback;
    // Actionable approval-row resolution (ported from SessionView): the
    // row id is a DB UUID independent of the MA event payload, so it is
    // polled while the gate is open. Skipped entirely under readOnly —
    // the cards render informational-only with no adapter calls.
    const approvalLookup = usePendingApprovalRow({
        sessionId,
        active: !readOnly && gateOpen,
        intervalMs: props.refreshIntervalMs ?? DEFAULT_APPROVAL_POLL_MS,
        preferredToolName: stream.pendingApproval?.toolName ?? null,
    });
    const handleSend = useCallback(async (content) => {
        // The epoch bumps ONLY on a session transition: a settling send
        // for the previous session must not mutate the new session's
        // composer, while same-session sends settle independently.
        const epoch = sendEpochRef.current;
        const live = () => sendEpochRef.current === epoch;
        pendingIdRef.current += 1;
        const pendingId = `${PENDING_BUBBLE_ID}-${pendingIdRef.current}`;
        setSending(true);
        setSendError(null);
        setPendingSends((prev) => [
            ...prev,
            {
                id: pendingId,
                content,
                // Baseline: matches already on the wire + earlier same-content
                // pending entries (each consumes one future echo).
                baseline: countEchoMatches(messagesRef.current, content) +
                    prev.filter((p) => p.content.trim() === content.trim()).length,
            },
        ]);
        try {
            await adapter.sendMessage({ sessionId, content });
        }
        catch (err) {
            if (live()) {
                // Drop THIS optimistic bubble; friendly copy only. A CP 409
                // `session_busy` (agent still responding) reads as `'busy'` — the
                // message wasn't sent, but nothing is broken — not a transport error.
                setPendingSends((prev) => prev.filter((p) => p.id !== pendingId));
                setSendError(classifySendError(err));
            }
        }
        finally {
            if (live())
                setSending(false);
        }
    }, [adapter, sessionId]);
    const items = useMemo(() => {
        const createdAtById = new Map(stream.events.map((e) => [e.id, e.createdAt]));
        // Per-turn durations (fn-49.3, R4): derived from the deduped event
        // log in the SAME memo pass as the items — no per-second re-render,
        // no reducer state. Only completed turns produce entries; the
        // in-flight turn shows nothing (the working indicator covers it).
        const turnDurationByEventId = new Map();
        for (const turn of computeTurnTimings(stream.events)) {
            turnDurationByEventId.set(turn.attachToEventId, formatDurationMs(turn.durationMs));
        }
        return buildChatItems(visibleMessages, createdAtById, turnDurationByEventId);
    }, [visibleMessages, stream.events]);
    // The in-flight token preview: `selectActivePreview` is the ONE seam over
    // the reducer's ephemeral slot (index order resolved there). Rendered as an
    // in-progress assistant bubble at the transcript tail; the durable
    // `agent.message` drops the buffer in the SAME reducer action that appends
    // the message, so two bubbles for one message cannot exist.
    const preview = selectActivePreview(stream);
    // Tail key: changes whenever a new message/step lands (id + count —
    // a step folding into an existing run still bumps the count). Growing
    // preview text is a new tail too, so `useStickToBottom`'s near-bottom gate
    // follows the stream without yanking a reader who scrolled up.
    const lastMessage = visibleMessages[visibleMessages.length - 1];
    const messagesTailKey = lastMessage !== undefined ? `${lastMessage.id}:${visibleMessages.length}` : null;
    const tailKey = preview !== null
        ? `${messagesTailKey ?? ''}|preview-${preview.eventId}:${preview.text.length}`
        : messagesTailKey;
    const loadFailed = row.failed || seed.failed || stream.lastError !== null;
    return (_jsxs("div", { className: "sbui-chat", children: [_jsxs("header", { className: "sbui-chat-header", children: [props.onBack !== undefined && (_jsx("button", { type: "button", className: "sbui-chat-back", onClick: props.onBack, children: BACK_LABEL })), _jsxs("div", { className: "sbui-chat-heading", children: [_jsx("h2", { className: "sbui-chat-subject", children: subject }), _jsx("p", { className: "sbui-chat-agent", children: agentName })] })] }), loadFailed && _jsx("output", { className: "sbui-chat-error", children: ERROR_COPY.generic }), reconnecting && (_jsx("output", { className: "sbui-chat-reconnect", "data-testid": "sbui-chat-reconnect", children: RECONNECTING_NOTE })), _jsx(ChatLog, { items: items, working: working, preview: preview, tailKey: tailKey, agentName: agentName }, `chat-log-${sessionId}`), gateOpen && (_jsx("div", { className: "sbui-chat-gate", "data-testid": "sbui-chat-gate", children: _jsx(ChatGateBody, { readOnly: readOnly, agentName: agentName, pending: stream.pendingApproval, approval: approvalLookup.approval, onDecided: approvalLookup.refresh, onRequestRefresh: approvalLookup.refresh }) })), terminal ? (_jsx("p", { className: "sbui-chat-ended", "data-testid": "sbui-chat-ended", children: CONVERSATION_ENDED })) : readOnly ? (_jsx("p", { className: "sbui-chat-view-only", "data-testid": "sbui-chat-view-only", children: CHAT_VIEW_ONLY })) : (_jsx(ChatComposer
            // Keyed by session: the uncontrolled draft is session-local.
            , { disabled: gateOpen, disabledReason: gateOpen ? RESPOND_TO_CONTINUE : null, 
                // Block SENDING (button + Enter) — but keep the textarea editable for
                // typing-ahead — while the agent is actively responding (status
                // `running`): a mid-turn send 409s on every backend. Suppressed while
                // the gate is open (that fully disables the composer already).
                busy: !gateOpen && working, busyReason: !gateOpen && working ? AGENT_RESPONDING : null, sending: sending, error: sendError === null ? null : ERROR_COPY[sendError], onSend: handleSend }, `chat-composer-${sessionId}`))] }));
}
/**
 * Renders the open gate's content:
 *
 *   - actionable (default): the approval row drives the card choice —
 *     `ack_kind === 'custom_tool_result_immediate'` (ask_user_question
 *     elicitation) mounts the interactive answer form; everything else
 *     mounts the plain-language approval card. No row yet → the
 *     awaiting note (a decide needs the row id; never an actionable
 *     skeleton).
 *   - `readOnly`: informational variants only, driven by the reducer's
 *     `pendingApproval` (no row was polled); no decision-capable
 *     controls exist and `decideApproval` is never called.
 *
 * Cards are KEYED BY APPROVAL ID: form/card state survives poll
 * refreshes (same id, fresh row object) and resets when a different
 * approval opens. The whole region unmounts when the stream ack
 * clears the gate.
 */
function ChatGateBody(props) {
    const { readOnly, agentName, pending, approval } = props;
    if (readOnly) {
        if (pending === null) {
            return _jsx("output", { className: "sbui-chat-gate-note", children: STATUS_LINES.awaiting_approval });
        }
        if (pending.toolName === ASK_USER_TOOL_NAME) {
            return (_jsx(AskUserForm, { agentName: agentName, source: pending.input, approval: null, readOnly: true }, `gate-form-${pending.toolUseId}`));
        }
        if (isReviewMatterTool(pending.toolName)) {
            return (_jsx(ReviewMatterCard, { agentName: agentName, matter: pending.input, approval: null, readOnly: true }, `gate-review-${pending.toolUseId}`));
        }
        return (_jsx(ApprovalActionCard, { agentName: agentName, toolName: pending.toolName, args: pending.input, approval: null, readOnly: true }, `gate-card-${pending.toolUseId}`));
    }
    if (approval === null) {
        // Awaiting the approval record (webhook lag) — the decide call
        // needs its DB id, so nothing actionable can render yet.
        return _jsx("output", { className: "sbui-chat-gate-note", children: STATUS_LINES.awaiting_approval });
    }
    if (approval.ack_kind === 'custom_tool_result_immediate') {
        // Immediate acks require form data. `review_matter` (fn-69.4) is a
        // confirmation tool whose assembled matter IS the form_data — render
        // the confirmation card sourced from the in-chat draft (or the row's
        // tool_args as the no-stream fallback). Guard on a tool-name match so
        // a mismatched row never renders another tool's payload.
        if (isReviewMatterTool(approval.tool_name)) {
            const matter = pending !== null && pending.toolName === approval.tool_name
                ? pending.input
                : approval.tool_args;
            return (_jsx(ReviewMatterCard, { agentName: agentName, matter: matter, approval: approval, readOnly: false, onDecided: props.onDecided, onRequestRefresh: props.onRequestRefresh }, `gate-review-${approval.id}`));
        }
        // Only ask_user_question has a renderable form; any OTHER immediate
        // custom tool gets the non-actionable fallback — never the bare
        // approval card, whose Approve would be rejected by the CP (missing
        // form_data).
        if (approval.tool_name !== ASK_USER_TOOL_NAME) {
            return _jsx(AskUserFallbackCard, {}, `gate-fallback-${approval.id}`);
        }
        // The reducer's pendingApproval.input is the in-chat questions
        // source; the row's tool_args is the no-stream fallback. Guard on
        // tool-name match so a mismatched row never renders another
        // tool's payload as a form.
        const source = pending !== null && pending.toolName === approval.tool_name
            ? pending.input
            : approval.tool_args;
        return (_jsx(AskUserForm, { agentName: agentName, source: source, approval: approval, readOnly: false, onDecided: props.onDecided, onRequestRefresh: props.onRequestRefresh }, `gate-form-${approval.id}`));
    }
    return (_jsx(ApprovalActionCard, { agentName: agentName, toolName: approval.tool_name, args: approval.tool_args, approval: approval, readOnly: false, onDecided: props.onDecided, onRequestRefresh: props.onRequestRefresh }, `gate-card-${approval.id}`));
}
/**
 * The scrollable message log: `role="log"` (implicit polite,
 * additions-only) with an accessible name; list semantics inside.
 * Decorations (working dots, scroll sentinel) are aria-hidden.
 */
function ChatLog(props) {
    const { containerRef, bottomRef, handleScroll } = useStickToBottom(props.tailKey);
    const reducedMotion = usePrefersReducedMotion();
    const lastItem = props.items[props.items.length - 1];
    return (_jsxs("div", { ref: containerRef, className: "sbui-chat-log", role: "log", "aria-label": conversationWith(props.agentName), onScroll: handleScroll, children: [_jsx("ul", { className: "sbui-chat-items", children: props.items.map((item) => (_jsx("li", { className: `sbui-chat-item sbui-chat-item--${item.kind}`, children: item.kind === 'bubbles' ? (_jsx(BubbleGroup, { group: item })) : (_jsx(ActivityBlock, { steps: item.steps, live: props.working && item === lastItem, duration: item.duration })) }, item.key))) }), props.preview !== null && (_jsx("div", { className: "sbui-chat-group sbui-chat-group--assistant sbui-chat-group--streaming", "data-testid": "sbui-chat-streaming", "aria-hidden": "true", children: _jsx("div", { className: "sbui-chat-bubble sbui-chat-bubble--assistant", children: _jsx(MarkdownContent, { content: props.preview.text }) }) })), props.working && props.preview === null && (_jsxs("div", { className: `sbui-chat-working${reducedMotion ? ' sbui-chat-working--static' : ''}`, "data-testid": "sbui-chat-working", "aria-hidden": "true", children: [_jsx("span", { className: "sbui-chat-working-dot" }), _jsx("span", { className: "sbui-chat-working-dot" }), _jsx("span", { className: "sbui-chat-working-dot" })] })), _jsx("div", { ref: bottomRef, className: "sbui-chat-bottom", "aria-hidden": "true" })] }));
}
//# sourceMappingURL=chat.js.map